import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Divider, InputAdornment, OutlinedInput } from '@mui/material';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useDispatch } from 'react-redux';
import BasicForm from '../basicForm/basicForm';
import colors from '../../../scss/colors';
import ButtonComponent from '../../common/buttons/ButtonComponent/ButtonComponent';
import { buttonVariants } from '../../common/buttons/types';
import Completed from '../../../Assets/images/completed.svg';
import { appNotificationsTypes, benefits, maxMobileSize } from '../../../utils/constants';
import Benefits from '../../common/Benefits/Benefits';
import { useWindowSize } from '../../../utils/hooks';
import api from '../../../api/apiSettings';
import { setNotification } from '../../../actions/appNotifications';
import { loginRequest } from '../../../authConfig';
import style from './finishForm.module.scss';

const labelStyle = { fontSize: '14px', color: colors.DARKEST_GREY };

const textFieldStyles = {
  backgroundColor: 'white',
  borderRadius: '5px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    height: '18px',
    padding: '12px 14px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #D1D3D4',
    borderRadius: '10px',
    padding: '12px'
  },
  color: colors.BLACK_TXT,
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

function FinishForm(props) {
  const { cancel, step, totalStep, data } = props;

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const size = useWindowSize();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pass, setPass] = useState('');

  const [registering, setRegistering] = useState(false);

  const [passValid, setPassValid] = useState({
    charactersqty: false,
    lowercase: false,
    uppercase: false,
    numberorsymbol: false
  });

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validate = (password) => {
    const initValid = {
      charactersqty: false,
      lowercase: false,
      uppercase: false,
      numberorsymbol: false
    };

    if (password.length >= 8) initValid.charactersqty = true;
    if (
      password
        .split('')
        .some(
          (smb) =>
            smb === smb.toLowerCase() && Number.isNaN(Number(smb)) && !smb.match(/[\][`~?!@#$%^&*\-+|/<>"':.,;]/g)
        )
    )
      initValid.lowercase = true;
    if (
      password
        .split('')
        .some(
          (smb) =>
            smb === smb.toUpperCase() && Number.isNaN(Number(smb)) && !smb.match(/[\][`~?!@#$%^&*\-+|/<>"':.,;]/g)
        )
    )
      initValid.uppercase = true;
    if (password.match(/\d+/g) || password.match(/[\][`~?!@#$%^&*\-+|/<>"':.,;]/g)) initValid.numberorsymbol = true;

    setPassValid(initValid);
  };

  const changePasswordHandler = (e) => {
    validate(e.target.value);
    setPass(e.target.value);
  };

  const registerHandler = async () => {
    const regData = {
      FirstName: data.firstName,
      LastName: data.lastName,
      Phone: data.Phone,
      PhoneCountryCode: data.PhoneCountryCode,
      PrimaryEmail: data.email,
      Password: pass
    };
    try {
      setRegistering(true);
      await api.post('v1/user/quick-registration', regData);
      instance.loginRedirect({ ...loginRequest, redirectUri: `${window.location.origin}/dashboard` }).catch((e) => {
        console.error(e);
      });
    } catch (e) {
      dispatch(
        setNotification({
          type: appNotificationsTypes.ERROR,
          message: e?.response?.data?.title || e.message,
          duration: 3000
        })
      );
    }
    setRegistering(false);
  };

  useEffect(() => {
    window.localStorage.setItem(`OpenedJobId`, '');
  }, []);

  return (
    <BasicForm cancel={cancel} step={step} totalStep={totalStep}>
      <div>
        <div className={style.head_wrapper}>
          {size.width > maxMobileSize && <img alt="Completed" className={style.completed_icon} src={Completed} />}
          <p className={`${style.header}`}>{t('jobs.quickapply.thankyou')}</p>
          <p className={`${style.sub_header}`}>{t('jobs.quickapply.applicationtorecruiter')}</p>
        </div>
        {size.width > maxMobileSize && <Divider />}
        <p className={style.benefitsText}>{t('jobs.quickapply.benefitstext')}</p>
        <Benefits
          passedBenefits={
            size.width > maxMobileSize
              ? [benefits.AI, benefits.EASY_APPLY, benefits.APP_TRACKING, benefits.AUTO_JOB_ALERTS]
              : [benefits.AI, benefits.EASY_APPLY, benefits.AUTO_JOB_ALERTS]
          }
          lightTheme
        />
        {size.width > maxMobileSize && <Divider sx={{ margin: '20px 0' }} />}
        <div className={style.form}>
          <div className={style.fieldContainer}>
            <p className={style.label}>E-mail</p>
            <TextField value={data.email} disabled fullWidth sx={textFieldStyles} />
          </div>
          <div className={style.fieldContainer}>
            <p className={style.label}>{t('common.general.password')}</p>
            <OutlinedInput
              fullWidth
              value={pass}
              onChange={changePasswordHandler}
              type={showPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              sx={textFieldStyles}
            />
          </div>
          <div className={style.checkboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={passValid.charactersqty}
                  name="At least 8 characters"
                  icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                  checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                />
              }
              label={t('common.passwordvalidation.charactersqty')}
              sx={labelStyle}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={passValid.lowercase}
                  name="One lowercase character"
                  icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                  checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                />
              }
              label={t('common.passwordvalidation.lowercase')}
              sx={labelStyle}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={passValid.uppercase}
                  name="One uppercase character"
                  icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                  checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                />
              }
              label={t('common.passwordvalidation.uppercase')}
              sx={labelStyle}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={passValid.numberorsymbol}
                  name="One number or symbol"
                  icon={<RadioButtonUncheckedIcon fontSize="14px" />}
                  checkedIcon={<RadioButtonCheckedIcon fontSize="14px" />}
                />
              }
              label={t('common.passwordvalidation.numberorsymbol')}
              sx={labelStyle}
            />
          </div>
        </div>
        <ButtonComponent
          disabled={Object.values(passValid).some((val) => !val) || registering}
          action={registerHandler}
          variant={buttonVariants.PRIMARY}
          classes="w-100 mt-4"
          text={registering ? t('common.general.registering') : t('jobs.quickapply.createanaccount')}
        />
        <div className={style.bottomContainer}>
          {size.width > maxMobileSize ? (
            <Link to="/jobs">{t('jobs.quickapply.returnjobboard')}</Link>
          ) : (
            <ButtonComponent
              action={() => navigate({ pathname: `/jobs` })}
              variant={buttonVariants.SECONDARY}
              classes="w-100"
              text={t('jobs.quickapply.returnjobboard')}
            />
          )}
        </div>
      </div>
    </BasicForm>
  );
}

export default FinishForm;
